import React, { Fragment, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Typography from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";
import { useParams } from "react-router";
import JSZip from "jszip";
import {
    validateCompressedFiles,
    protoFilesCounter,
    protoFilesInsideFolderPattern,
    protoFileRegexPattern,
} from "../../../../../../Utils/ValidateCompressedFiles";
import { SNETFileUpload, AlertBox, alertTypes } from "shared/dist/components";
import {
    aiServiceDetailsActions,
    loaderActions,
} from "../../../../../../Services/Redux/actionCreators";
import { assetTypes, fileTypes } from "../../../../../../Utils/FileUpload";
import ValidationError from "shared/dist/utils/validationError";
import { checkIfKnownError } from "shared/dist/utils/error";
import { errorStatus, protoAlerts, protoStatusTypes } from "./ProtoFilesStatus";
import { LoaderContent } from "../../../../../../Utils/Loader";
import { serviceTypes } from "../../../../../../Utils/serviceTypes";
import classes from "./UploadProto.module.css";
import clsx from "clsx";
// import { useProtoFileCountSelector } from "../../../../../../Services/Redux/selectors/aiServiceDetailsSelector";

const protoStatusTimeout = 1500;

function mapProtoFileToSelected(protoFiles) {
    if (!protoFiles?.url) {
        return {
            name: "",
            size: "",
            type: "",
        };
    }
    const fileName = protoFiles.url.split("/").at(-1);
    return {
        name: fileName,
        size: "-",
        type: fileName.split(".")[1],
    };
}

const UploadProto = ({ isFreezedByActivatedHaas }) => {
    const protoFiles = useSelector((state) => state.aiServiceDetails.assets.protoFiles);
    const serviceType = useSelector((state) => state.aiServiceDetails.serviceType);
    // const backendProtoFileCount = useProtoFileCountSelector();
    const [localProtoFileCount, setLocalProtoFileCount] = useState();

    const dispatch = useDispatch();

    const [alert, setAlert] = useState({});

    const selectedFileByProtoFiles = useMemo(
        () => mapProtoFileToSelected(protoFiles),
        [protoFiles]
    );

    const [selectedFilePricing, setSelectedFilePricing] = useState();
    const { orgUuid, serviceUuid } = useParams();

    useEffect(() => {
        if (serviceType === serviceTypes.HTTP && localProtoFileCount > 1)
            setAlert({
                type: alertTypes.ERROR,
                message: errorStatus.httpMoreThanOneProto,
            });
    }, [serviceType, localProtoFileCount]);

    useEffect(() => {
        const protoStatus = protoFiles.status;
        Boolean(protoStatus) && setAlertByProtoStatus(protoStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeProtoFiles = (url) => {
        dispatch(aiServiceDetailsActions.setServiceTouchedFlag(true));
        dispatch(aiServiceDetailsActions.setServiceDetailsProtoUrl(url));
    };

    const getProtoStatus = async () => {
        try {
            const protoStatus = await dispatch(
                aiServiceDetailsActions.getHandlingFileStatus(
                    orgUuid,
                    serviceUuid,
                    fileTypes.protoFiles
                )
            );
            return protoStatus;
        } catch (error) {
            setAlert({
                type: alertTypes.ERROR,
                message: errorStatus.tryAgain,
            });
        }
    };

    const validateProtoFile = (uploadedFile) => {
        return new Promise((resolve, reject) => {
            const zip = new JSZip();
            zip.loadAsync(uploadedFile).then((entry) => {
                const filesInsideSomeFolder = validateCompressedFiles(
                    protoFilesInsideFolderPattern,
                    entry
                );

                const protoFilesCount = protoFilesCounter(entry);

                setLocalProtoFileCount(protoFilesCount);
                if (serviceType === serviceTypes.HTTP && protoFilesCount > 1) {
                    reject(new ValidationError(errorStatus.httpMoreThanOneProto));
                }
                if (filesInsideSomeFolder) {
                    reject(new ValidationError(errorStatus.filesInsideSomeFolder));
                } else if (!validateCompressedFiles(protoFileRegexPattern, entry, true)) {
                    reject(new ValidationError(errorStatus.protoNotFound));
                } else {
                    resolve();
                }
            });
        });
    };

    const getProtoStatusTimeout = () => {
        let protoStatus;
        let timerId = setTimeout(async function setProtoStatusTimeout() {
            protoStatus = await getProtoStatus();
            if (protoStatus === protoStatusTypes.PENDING) {
                timerId = setTimeout(setProtoStatusTimeout, protoStatusTimeout);
            } else {
                dispatch(loaderActions.stopAppLoader());
                clearTimeout(timerId);
            }
            setAlertByProtoStatus(protoStatus);
        }, protoStatusTimeout);
    };

    const setAlertByProtoStatus = (protoStatus) => {
        const protoAlert = protoAlerts[protoStatus];
        setAlert(protoAlert);
    };

    const handleDrop = async (acceptedFiles, rejectedFiles) => {
        setAlert({});
        if (!isEmpty(rejectedFiles)) {
            return setAlert({
                type: alertTypes.ERROR,
                message: errorStatus.maxSizeAndType,
            });
        }
        if (!isEmpty(acceptedFiles)) {
            try {
                const fileBlob = acceptedFiles[0];
                await validateProtoFile(fileBlob);
                const { name, size, type } = fileBlob;
                setSelectedFilePricing({ name, size, type });
                dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
                const { url } = await dispatch(
                    aiServiceDetailsActions.uploadFile(
                        assetTypes.SERVICE_PROTO_FILES,
                        fileBlob,
                        orgUuid,
                        serviceUuid
                    )
                );
                changeProtoFiles(url);
                setAlertByProtoStatus(protoStatusTypes.PENDING);
                dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
                getProtoStatusTimeout();
            } catch (error) {
                if (checkIfKnownError(error)) {
                    return setAlert({ type: alertTypes.ERROR, message: error.message });
                }
                setAlert({ type: alertTypes.ERROR, message: errorStatus.default });
            } finally {
                dispatch(loaderActions.stopAppLoader());
            }
        }
    };

    const acceptedFileTypes = {
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
    };

    return (
        <Fragment>
            <Typography variant="subtitle1" color={isFreezedByActivatedHaas && "text.disabled"}>
                Upload the Proto files {serviceType}
            </Typography>
            <Typography
                className={clsx(classes.description, isFreezedByActivatedHaas && classes.disabled)}
            >
                Services define their API using protocol buffers. This allows SingularityNET clients
                to determine the request/response schema programmatically. Read more&nbsp;
                <a
                    href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/DevelopersTutorials/IntegrationGRPCService/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    here
                </a>
                .
            </Typography>
            <Typography
                className={clsx(classes.description, isFreezedByActivatedHaas && classes.disabled)}
            >
                The http service type supports only one proto file. Read more&nbsp;
                <a
                    href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/DevelopersTutorials/IntegrationHTTPService/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    here
                </a>
                .
            </Typography>
            <SNETFileUpload
                onDrop={handleDrop}
                accept={acceptedFileTypes}
                multiple={false}
                showFileDetails
                helperText={
                    <>
                        <Typography>
                            * Compress only the individual files with no parent folders
                        </Typography>
                        <Typography>* Package must be under 2mb</Typography>
                        <Typography>* Make sure the extension is .zip</Typography>
                    </>
                }
                fileName={(selectedFilePricing ?? selectedFileByProtoFiles).name}
                fileSize={(selectedFilePricing ?? selectedFileByProtoFiles).size}
                fileDownloadURL={protoFiles.url}
                uploadSuccess={Boolean(protoFiles.url && alert?.type !== alertTypes.ERROR)}
                disabled={isFreezedByActivatedHaas}
            />
            {alert?.message && (
                <div className={classes.errorContainer}>
                    <AlertBox type={alert?.type} message={alert?.message} />
                </div>
            )}
        </Fragment>
    );
};

export default UploadProto;
